

import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";

/**
 * AuthContext - Contexto global para manejar autenticación
 * Proporciona estado de sesión, funciones de login/logout y validación
 * INCLUYE: Control de sesión única (una sola sesión activa por usuario)
 */
const AuthContext = createContext();

// Generar token de sesión único
const generarSessionToken = () => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [sesionCerradaRemotamente, setSesionCerradaRemotamente] = useState(false);
  const sessionTokenRef = useRef(null);
  const subscripcionRef = useRef(null);

  // Cargar sesión al montar el componente
  useEffect(() => {
    const cargarSesion = async () => {
      try {
        // Solo ejecutar en cliente (navegador), no en servidor
        if (typeof window === "undefined") {
          setCargando(false);
          return;
        }

        // Verificar si hay token guardado en sessionStorage (se limpia al cerrar el navegador)
        const tokenGuardado = sessionStorage.getItem("authToken");
        const usuarioGuardado = sessionStorage.getItem("usuarioActual");
        const sessionTokenGuardado = sessionStorage.getItem("sessionToken");

        if (tokenGuardado && usuarioGuardado && sessionTokenGuardado) {
          try {
            const usuario = JSON.parse(usuarioGuardado);
            
            // Verificar que el session_token en la BD coincida con el local
            const { data: usuarioDB } = await supabase
              .from("usuarios")
              .select("session_token, activo")
              .eq("id", usuario.id)
              .single();
            
            if (usuarioDB && usuarioDB.session_token === sessionTokenGuardado && usuarioDB.activo) {
              // Sesión válida
              sessionTokenRef.current = sessionTokenGuardado;
              setUsuario(usuario);
              
              // Iniciar escucha de cambios en session_token
              iniciarEscuchaSesion(usuario.id, sessionTokenGuardado);
            } else {
              // Sesión inválida (se abrió otra sesión o usuario inactivo)
              console.log("⚠️ Sesión invalidada: otra sesión activa o usuario inactivo");
              sessionStorage.removeItem("authToken");
              sessionStorage.removeItem("usuarioActual");
              sessionStorage.removeItem("sessionToken");
              if (usuarioDB && usuarioDB.session_token !== sessionTokenGuardado) {
                setSesionCerradaRemotamente(true);
              }
            }
          } catch (parseError) {
            console.error("Error parseando usuario:", parseError);
            sessionStorage.removeItem("authToken");
            sessionStorage.removeItem("usuarioActual");
            sessionStorage.removeItem("sessionToken");
          }
        }
      } catch (error) {
        console.error("Error cargando sesión:", error);
        setError("Error al cargar la sesión");
      } finally {
        setCargando(false);
      }
    };

    cargarSesion();
    
    // Cleanup: remover suscripción al desmontar
    return () => {
      if (subscripcionRef.current) {
        subscripcionRef.current.unsubscribe();
      }
    };
  }, []);

  /**
   * Iniciar escucha en tiempo real de cambios en session_token
   * Si cambia, significa que se abrió otra sesión y esta debe cerrarse
   */
  const iniciarEscuchaSesion = (usuarioId, tokenActual) => {
    // Remover suscripción anterior si existe
    if (subscripcionRef.current) {
      subscripcionRef.current.unsubscribe();
    }

    subscripcionRef.current = supabase
      .channel(`sesion-unica-${usuarioId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'usuarios',
          filter: `id=eq.${usuarioId}`
        },
        (payload) => {
          const nuevoToken = payload.new.session_token;
          const activo = payload.new.activo;
          
          // Si el token cambió o el usuario fue desactivado, cerrar sesión
          if (nuevoToken !== sessionTokenRef.current || !activo) {
            console.log("🔒 Sesión cerrada: se detectó otra sesión activa o usuario desactivado");
            setSesionCerradaRemotamente(true);
            cerrarSesionLocal();
          }
        }
      )
      .subscribe();
  };

  /**
   * Cerrar sesión localmente (sin actualizar BD)
   */
  const cerrarSesionLocal = () => {
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("usuarioActual");
    sessionStorage.removeItem("sessionToken");
    sessionTokenRef.current = null;
    setUsuario(null);
    
    if (subscripcionRef.current) {
      subscripcionRef.current.unsubscribe();
      subscripcionRef.current = null;
    }
  };

  /**
   * Función de login - Valida credenciales contra Supabase
   * Implementa SESIÓN ÚNICA: cierra sesiones anteriores automáticamente
   * @param {string} usuario - Usuario (documento o código)
   * @param {string} clave - Clave/contraseña
   */
  const login = async (usuario, clave) => {
    setError(null);
    setCargando(true);
    setSesionCerradaRemotamente(false);

    try {
      // Validaciones básicas
      if (!usuario || !clave) {
        throw new Error("Usuario y clave son requeridos");
      }

      // Buscar usuario en tabla 'usuarios' de Supabase
      const { data, error: queryError } = await supabase
        .from("usuarios")
        .select("*")
        .eq("usuario", usuario.toString())
        .single();

      if (queryError || !data) {
        throw new Error("Usuario o clave inválidos");
      }

      // Validar clave (en producción usar hash bcrypt)
      // Por ahora comparación directa (mejorar en producción)
      if (data.clave.toString() !== clave.toString()) {
        throw new Error("Usuario o clave inválidos");
      }

      // Verificar si el usuario está activo
      if (!data.activo) {
        throw new Error("Usuario inactivo. Contacte al administrador.");
      }

      // Generar nuevo token de sesión único
      // Esto invalidará cualquier sesión anterior automáticamente
      const nuevoSessionToken = generarSessionToken();

      // Crear objeto de sesión
      const sesion = {
        id: data.id,
        usuario: data.usuario,
        nombre: data.nombre,
        rol: data.perfil || "usuario",
        permisos: data.permisos || [],
        creadoEn: data.created_at,
        ultimoAcceso: new Date().toISOString(),
      };

      // Guardar session_token en Supabase (esto cerrará otras sesiones)
      await supabase
        .from("usuarios")
        .update({ 
          session_token: nuevoSessionToken,
          updated_at: new Date().toISOString() 
        })
        .eq("id", data.id);

      // Guardar en sessionStorage (se limpia al cerrar el navegador)
      const token = btoa(`${usuario}:${data.id}:${Date.now()}`); // Token simple (mejorar)
      sessionStorage.setItem("authToken", token);
      sessionStorage.setItem("usuarioActual", JSON.stringify(sesion));
      sessionStorage.setItem("sessionToken", nuevoSessionToken);
      
      // Guardar referencia del token
      sessionTokenRef.current = nuevoSessionToken;

      setUsuario(sesion);
      
      // Iniciar escucha de cambios en session_token
      iniciarEscuchaSesion(data.id, nuevoSessionToken);

      return { exito: true, usuario: sesion };
    } catch (err) {
      const mensajeError = err.message || "Error al iniciar sesión";
      setError(mensajeError);
      return { exito: false, error: mensajeError };
    } finally {
      setCargando(false);
    }
  };

  /**
   * Función de logout - Limpia la sesión
   */
  const logout = async () => {
    try {
      // Si hay un usuario, limpiar session_token en Supabase
      if (usuario) {
        await supabase
          .from("usuarios")
          .update({
            session_token: null,
            ultimo_acceso: new Date().toISOString(),
          })
          .eq("id", usuario.id);
      }

      // Limpiar sessionStorage
      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("usuarioActual");
      sessionStorage.removeItem("sessionToken");
      
      // Limpiar ref y suscripción
      sessionTokenRef.current = null;
      if (subscripcionRef.current) {
        subscripcionRef.current.unsubscribe();
        subscripcionRef.current = null;
      }

      setUsuario(null);
      setError(null);
      setSesionCerradaRemotamente(false);
      return { exito: true };
    } catch (err) {
      console.error("Error en logout:", err);
      // Limpiar de todas formas
      cerrarSesionLocal();
      return { exito: true };
    }
  };

  /**
   * Validar si el usuario tiene un permiso específico
   * @param {string} permiso - Nombre del permiso
   */
  const tienePermiso = (permiso) => {
    if (!usuario) return false;
    return usuario.permisos.includes(permiso) || usuario.rol === "admin";
  };

  /**
   * Refrescar datos del usuario desde Supabase
   */
  const refrescarUsuario = async () => {
    if (!usuario) return;

    try {
      const { data } = await supabase
        .from("usuarios")
        .select("*")
        .eq("id", usuario.id)
        .single();

      if (data) {
        const sesionActualizada = {
          ...usuario,
          permisos: data.permisos || [],
          rol: data.rol || "usuario",
        };
        setUsuario(sesionActualizada);
        sessionStorage.setItem("usuarioActual", JSON.stringify(sesionActualizada));
      }
    } catch (err) {
      console.error("Error refrescando usuario:", err);
    }
  };

  /**
   * Limpiar alerta de sesión cerrada remotamente
   */
  const limpiarAlertaSesion = () => {
    setSesionCerradaRemotamente(false);
  };

  const value = {
    usuario,
    cargando,
    error,
    login,
    logout,
    tienePermiso,
    refrescarUsuario,
    estaAutenticado: !!usuario,
    sesionCerradaRemotamente,
    limpiarAlertaSesion,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};
